import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useRef } from "react";
import { z } from "zod";

import { MiniSprintOfferPage } from "~/components/offer-page/mini-sprint-offer-page";
import { OfferCoachSection } from "~/components/offer-page/offer-coach-section";
import { OfferCurriculumSection } from "~/components/offer-page/offer-curriculum-section";
import { OfferFaqSection } from "~/components/offer-page/offer-faq-section";
import { OfferHero } from "~/components/offer-page/offer-hero";
import { OfferHowSection } from "~/components/offer-page/offer-how-section";
import { OfferIncludedSection } from "~/components/offer-page/offer-included-section";
import { OfferOutcomeSection } from "~/components/offer-page/offer-outcome-section";
import { OfferPricingCta } from "~/components/offer-page/offer-pricing-cta";
import { OfferProblemSection } from "~/components/offer-page/offer-problem-section";
import { OfferProof } from "~/components/offer-page/offer-proof";
import { OfferStickyCta } from "~/components/offer-page/offer-sticky-cta";
import { ButtonRouter } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { useOfferCheckout } from "~/hooks/use-offer-checkout";
import { pickOfferCta } from "~/lib/offer/cta";
import { buildOfferDatePills } from "~/lib/offer/dates";
import { computeOfferSectionNumbers } from "~/lib/offer/section-numbering";
import { OfferStatus, OfferType } from "~/lib/schemas/db-enums";
import { isCoachIndexable } from "~/lib/shared/coach-indexability";
import { toMajorUnits } from "~/lib/shared/currency";
import { formatPrice } from "~/lib/shared/format-price";
import { createMetaTags, createServiceStructuredData } from "~/lib/shared/meta";
import { getOfferByUsernameAndId } from "~/shared/loaders";

const OfferPageSearchSchema = z.object({
  success: z.literal("true").optional(),
});

const OfferPageSearchParser = OfferPageSearchSchema.optional();

type OfferPageSearch = z.infer<typeof OfferPageSearchSchema>;

export const Route = createFileRoute("/$username/offer/$offerId/")({
  loader: async ({ params }) => {
    const result = await getOfferByUsernameAndId({
      data: { username: params.username, offerId: params.offerId },
    });
    if (result.kind === "not_found") {
      throw notFound();
    }
    // A draft or retired offer: send visitors with a shared link to the
    // coach's profile, where they can still find the coach.
    if (result.kind === "unavailable") {
      throw redirect({
        to: "/$username",
        params: { username: params.username },
      });
    }
    return result;
  },
  validateSearch: (search): OfferPageSearch => {
    const result = OfferPageSearchParser.parse(search);
    return result ?? {};
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return createMetaTags({
        title: "Coaching Offer | Dream Coach Match",
        description: "View this coaching offer",
        noindexFollow: true,
      });
    }

    const { coach, offer } = loaderData;
    const coachName = coach?.fullName || params.username;
    const offerTitle = offer?.headline || "Coaching Offer";
    const description =
      offer?.description ||
      `Discover ${coachName}'s coaching offer designed to help you achieve your goals.`;
    const imageUrl =
      offer?.vslVideoUrl ||
      coach?.profile?.mainImage ||
      coach?.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/offer/${params.offerId}`;

    const metaTags = createMetaTags({
      title: `${offerTitle} - ${coachName} | Dream Coach Match`,
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "product",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coachName,
      keywords: [
        "coaching",
        "offer",
        "service",
        offer?.tags?.map((t) => t.title).join(", ") || "",
        coach?.profile?.coachingTitle || "life coach",
      ].filter(Boolean),
    });

    // Add structured data for the service
    const structuredData = createServiceStructuredData({
      name: offerTitle,
      description,
      provider: coachName,
      url: currentUrl,
      price: offer?.price
        ? toMajorUnits(offer.price, offer.currency ?? "USD").toString()
        : undefined,
      image: imageUrl || undefined,
    });

    // Offer pages no longer sit under the `_profile` layout, so the noindex
    // rule for non-indexable coaches is applied here directly. The coach's
    // preview of an unpublished offer is never indexable either.
    const shouldIndex =
      !!coach &&
      isCoachIndexable(coach) &&
      offer.status === OfferStatus.Published;

    return {
      ...metaTags,
      meta: [
        ...(metaTags.meta ?? []),
        ...(shouldIndex
          ? []
          : [{ name: "robots", content: "noindex, nofollow" }]),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  notFoundComponent: OfferNotFound,
  component: OfferPage,
});

function OfferNotFound() {
  const { username } = Route.useParams();
  return (
    <div className="flex-basis-0 flex flex-grow-6 flex-col items-stretch gap-8">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <h1 className="font-heading text-3xl">Offer not found</h1>
          <p className="text-text-secondary max-w-md">
            This offer doesn't exist or is no longer available.
          </p>
          <ButtonRouter to="/$username" params={{ username }} variant="primary">
            Back to profile
          </ButtonRouter>
        </CardContent>
      </Card>
    </div>
  );
}

function OfferPage() {
  const { coach, offer } = Route.useLoaderData();

  return (
    <>
      {offer.status !== OfferStatus.Published && (
        <UnpublishedOfferNotice status={offer.status} />
      )}
      {offer.type === OfferType.MiniSprint ? (
        <MiniSprintOfferPage coach={coach} offer={offer} />
      ) : (
        <StandardOfferPage coach={coach} offer={offer} />
      )}
    </>
  );
}

/** Only the coach reaches an unpublished offer page; tell them why it's hidden. */
function UnpublishedOfferNotice({
  status,
}: {
  status: Exclude<OfferStatus, OfferStatus.Published>;
}) {
  const isRetired = status === OfferStatus.Retired;
  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 xl:px-0">
      <div role="status" className="rounded-lg bg-amber-50 p-4 text-amber-800">
        <p className="font-medium">
          {isRetired ? "This offer is retired" : "This offer is a draft"}
        </p>
        <p className="text-sm">
          {isRetired
            ? "Only you can see this page. Visitors are sent to your profile, and nobody can buy the offer."
            : "Only you can see this page. Publish the offer to show it on your profile and let clients buy it."}
        </p>
      </div>
    </div>
  );
}

type LoaderData = Pick<
  Extract<
    Awaited<ReturnType<typeof getOfferByUsernameAndId>>,
    { kind: "found" }
  >,
  "coach" | "offer"
>;

function StandardOfferPage({ coach, offer }: LoaderData) {
  const { createCheckout } = useOfferCheckout();
  const search: OfferPageSearch = Route.useSearch();
  const ctaRef = useRef<HTMLDivElement>(null);

  const onBuy = () => createCheckout.mutate(offer.id);
  const isBuying = createCheckout.isPending;
  const checkoutError = createCheckout.error?.message ?? null;
  const showSuccess = search.success === "true";

  const cta = pickOfferCta({ offer, coach });

  const credentialCount =
    (coach.ccoMemberships?.length ?? 0) +
    (coach.ctpCertifications?.length ?? 0) +
    (coach.certifications?.length ?? 0);

  const nums = computeOfferSectionNumbers({
    offer,
    coachBio: coach.profile.description,
    resultsCount: coach.results?.length ?? 0,
    benefitsCount: offer.benefits.length,
    componentsCount: offer.components.length,
    bonusesCount: offer.bonuses.length,
    faqsCount: offer.faqs.length,
    credentialCount,
  });

  const datePills = buildOfferDatePills({
    applicationDeadline: offer.applicationDeadline,
    cohortStartDate: offer.cohortStartDate,
  });

  return (
    <>
      {showSuccess && (
        <div className="mx-auto max-w-7xl px-4 pt-4 xl:px-0">
          <div className="rounded-lg bg-green-50 p-4 text-green-700">
            <p className="font-medium">Thank you for your purchase!</p>
            <p className="text-sm">
              You will receive a confirmation email shortly.
            </p>
          </div>
        </div>
      )}

      <OfferHero
        coach={coach}
        offer={offer}
        cta={cta}
        onBuy={onBuy}
        isBuying={isBuying}
        ctaRef={ctaRef}
      />

      {nums.problem !== undefined && (
        <OfferProblemSection
          headline={offer.strugglesHeadline}
          description={offer.strugglesDescription}
          imageUrl={offer.strugglesImageUrl}
          sectionNumber={nums.problem}
        />
      )}

      {nums.outcome !== undefined && (
        <OfferOutcomeSection
          headline={offer.resultsHeadline}
          description={offer.resultsDescription}
          imageUrl={offer.resultsImageUrl}
          results={coach.results ?? []}
          sectionNumber={nums.outcome}
          cta={cta}
          onBuy={onBuy}
          isBuying={isBuying}
        />
      )}

      {nums.coach !== undefined && (
        <OfferCoachSection
          firstName={coach.firstName}
          bio={coach.profile.description}
          avatarUrl={coach.profilePictureUrl}
          ccoMemberships={coach.ccoMemberships ?? []}
          ctpCertifications={coach.ctpCertifications ?? []}
          certifications={coach.certifications ?? []}
          sectionNumber={nums.coach}
          cta={cta}
          onBuy={onBuy}
          isBuying={isBuying}
        />
      )}

      {nums.how !== undefined && (
        <OfferHowSection
          headline={offer.programHeadline}
          description={offer.programDescription}
          imageUrl={offer.programImageUrl}
          benefits={offer.benefits}
          sectionNumber={nums.how}
        />
      )}

      <OfferCurriculumSection
        items={offer.curriculum}
        durationLabel={offer.durationLabel}
      />

      {nums.included !== undefined && (
        <OfferIncludedSection
          components={offer.components}
          bonuses={offer.bonuses}
          sectionNumber={nums.included}
        />
      )}

      <OfferProof testimonials={coach.testimonials ?? []} />

      <OfferPricingCta
        headline={offer.bottomCtaHeadline}
        price={offer.price}
        originalPrice={offer.originalPrice}
        currency={offer.currency}
        guaranteeText={offer.satisfactionGuaranteeText}
        datePills={datePills}
        cta={cta}
        onBuy={onBuy}
        isBuying={isBuying}
        checkoutError={checkoutError}
      />

      {nums.faq !== undefined && (
        <OfferFaqSection faqs={offer.faqs} sectionNumber={nums.faq} />
      )}

      <OfferStickyCta
        triggerRef={ctaRef}
        offerName={offer.headline ?? "This offer"}
        priceLabel={
          offer.price && offer.currency
            ? formatPrice(offer.price, offer.currency)
            : null
        }
        coachAvatarUrl={coach.profilePictureUrl}
        coachName={coach.firstName}
        cta={cta}
        onBuy={onBuy}
        isBuying={isBuying}
      />
    </>
  );
}
